import type { RefObject } from 'react';
import { useCallback, useEffect, useRef } from 'react';

/* Long enough for a smooth scroll or a flick to come to rest, short enough that
   the event is not reported noticeably after the movement. */
const SETTLE_MS = 150;

export interface CarouselNavigation {
  jumpTo: (left: number) => void;
  scrollNext: () => void;
  scrollPrev: () => void;
}

/** What a settled movement was, and what caused it. */
export interface CarouselNavigatePayload {
  direction: 'next' | 'prev';
  method: 'arrow' | 'swipe';
}

/**
 * Scrolls the track by one item, and reports every settled movement once.
 *
 * There is a single place a navigation is reported: the track has come to rest
 * somewhere new. Pressing an arrow only records what the next movement should be
 * called. The alternative, reporting the arrow press directly and then
 * suppressing the scroll it causes, is what the Ribbons carousel does, and it is
 * where its two reporting bugs come from: a resize fires a scroll event and is
 * reported as a swipe, and a suppression flag that is never cleared swallows the
 * next real one.
 */
export const useCarouselNavigation = (
  trackRef: RefObject<HTMLElement>,
  onNavigate: ((payload: CarouselNavigatePayload) => void) | undefined,
): CarouselNavigation => {
  const callback = useRef(onNavigate);
  callback.current = onNavigate;

  /* What to call the next settled movement. Anything the component did not
     initiate is the customer swiping. */
  const method = useRef<'arrow' | 'swipe'>('swipe');
  const lastScrollLeft = useRef(0);

  useEffect(() => {
    const track = trackRef.current;

    if (!track) {
      return undefined;
    }

    let settle: ReturnType<typeof setTimeout>;

    const handleScroll = () => {
      clearTimeout(settle);

      settle = setTimeout(() => {
        const { scrollLeft } = track;
        const delta = scrollLeft - lastScrollLeft.current;

        lastScrollLeft.current = scrollLeft;

        const settledMethod = method.current;
        method.current = 'swipe';

        /* A resize fires a scroll event without the track having moved, and a `jumpTo`
           has already recorded its landing place as the baseline. Nothing the customer
           did, so nothing is reported. */
        if (delta === 0) {
          return;
        }

        callback.current?.({ direction: delta > 0 ? 'next' : 'prev', method: settledMethod });
      }, SETTLE_MS);
    };

    track.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      clearTimeout(settle);
      track.removeEventListener('scroll', handleScroll);
    };
  }, [trackRef]);

  const scrollByItem = useCallback(
    (direction: 1 | -1) => {
      const track = trackRef.current;
      const left = track ? direction * itemStep(track) : 0;

      /* Nothing to move by, so nothing is named an arrow press. The method names
         the next settled movement, and a press that moves the track nowhere
         fires no scroll event to clear it, so it would name whatever the
         customer does next instead. */
      if (!track || !left || !track.scrollBy) {
        return;
      }

      method.current = 'arrow';
      track.scrollBy({ behavior: 'smooth', left });
    },
    [trackRef],
  );

  const scrollNext = useCallback(() => scrollByItem(1), [scrollByItem]);
  const scrollPrev = useCallback(() => scrollByItem(-1), [scrollByItem]);

  /* Moves the track without reporting a navigation. A bare `scrollLeft` assignment fires
     a real scroll event, and with the baseline still at 0 the settle handler would call
     it a customer swipe, so a card that opens on its third photo would report a phantom
     "next". Recording the new position as the baseline makes the settled delta 0, which
     the handler already treats as nothing having happened. Read back rather than trusted,
     because the browser clamps the assignment to the scrollable range. */
  const jumpTo = useCallback(
    (left: number) => {
      const track = trackRef.current;

      if (!track) {
        return;
      }

      track.scrollLeft = left;
      lastScrollLeft.current = track.scrollLeft;
    },
    [trackRef],
  );

  return { jumpTo, scrollNext, scrollPrev };
};

/**
 * How far one press moves the track: the distance between two items, which
 * already includes the gap.
 *
 * Measured from the rendered items rather than worked out from `scrollWidth`
 * divided by the item count, so it stays right at whatever width the `itemSize`
 * step resolves to, and does not assume every item is the same width.
 */
const itemStep = (track: HTMLElement) => {
  const [first, second] = Array.from(track.children) as HTMLElement[];

  if (second) {
    return second.offsetLeft - first.offsetLeft;
  }

  return first?.offsetWidth ?? 0;
};
